(() => {
  if (window.SportsZoneCartConfig?.initialized) return;

  const SOURCE = 'sportszone-cart-config';
  const CONFIG_PROP = '_SportsZone configuration';
  const PARENT_PROP = '_SportsZone parent';
  const COMPONENT_PROP = '_SportsZone component';
  const SERVICE_VARIANT_PROP = '_SportsZone service variant';
  const SERVICE_FOR_PROP = '_SportsZone service for';
  const SERVICE_PROFILE_PROP = '_SportsZone service profile';
  const SERVICE_SHARPEN_PROP = '_SportsZone service sharpening';
  const STEEL_VARIANT_PROP = '_SportsZone steel variant';
  const STEEL_FOR_PROP = '_SportsZone steel for';
  const X_PACKAGE_PROP = '_SportsZone FLY-X package';
  const X_COMPONENT_PROP = '_SportsZone FLY-X component';
  const TI_PACKAGE_PROP = '_SportsZone FLY-TI package';
  const TI_COMPONENT_PROP = '_SportsZone FLY-TI component';
  const X_CODE = 'SZ-FLYX-PKG-ADJ-5H8M3T7';
  const TI_CODE = 'SZ-FLYTI-PKG-ADJ-6T2R8M4';
  const MANAGED_CODES = new Set([X_CODE, TI_CODE, 'SZ-FLYX80-7K4M2', 'SZ-FLYX-RUNNER-PKG-9Q7K4N2']);
  const REQUIRED_CONFIGURATION_HANDLES = new Set([
    'bauer-vapor-flylite-senior-hockey-skates',
    'bauer-vapor-flylite-red-senior-hockey-skates',
    'bauer-vapor-flylite-intermediate-hockey-skates',
    'bauer-vapor-flypro-senior-hockey-skates',
    'bauer-vapor-flypro-intermediate-hockey-skates',
    'bauer-supreme-fuse-senior-skate',
    'bauer-supreme-fuse-intermediate-hockey-skates',
    'bauer-supreme-f50-pro-senior-hockey-skates',
    'bauer-supreme-f50-pro-intermediate-hockey-skates',
    'bauer-supreme-m50-pro-senior-hockey-skates',
    'bauer-supreme-m50-pro-intermediate-hockey-skates',
    '1059765',
    '1059766',
    '1061682',
    '1061683',
    '1061681',
    '1061726',
    '1062089',
    'bauer-vapor-xltx-pro-plus-intermediate-hockey-skates-2023',
    'bauer-supreme-shadow-senior-hockey-skates',
    'bauer-supreme-shadow-intermediate-hockey-skates'
  ]);
  const originalFetch = window.fetch.bind(window);
  const rootUrl = () => window.Shopify?.routes?.root || '/';
  const money = (cents) => {
    const amount = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'CAD';
    const locale = document.documentElement.lang || 'en-CA';
    try { return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount); }
    catch (_error) { return `$${amount.toFixed(2)}`; }
  };
  const pathOf = (input) => {
    const value = typeof input === 'string' || input instanceof URL ? String(input) : String(input?.url || '');
    try { return new URL(value, location.origin).pathname; }
    catch (_error) { return value.split('?')[0]; }
  };
  const isAdd = (input) => /\/cart\/add(?:\.js)?\/?$/.test(pathOf(input));
  const isMutation = (input) => /\/cart\/(?:add|change|update|clear)(?:\.js)?\/?$/.test(pathOf(input));
  const props = (item) => item?.properties && typeof item.properties === 'object' ? item.properties : {};
  const idNum = (value) => Number(String(value || '').match(/(\d+)$/)?.[1] || 0);
  const uniqueKey = (variantId) => `sz-${idNum(variantId)}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const isPackageComponent = (p) => Boolean(p?.[X_COMPONENT_PROP] || p?.[TI_COMPONENT_PROP]);
  const isXParent = (item) => Boolean(props(item)[X_PACKAGE_PROP]);
  const isTiParent = (item) => Boolean(props(item)[TI_PACKAGE_PROP]);
  const isGenericParent = (item) => Boolean(props(item)[CONFIG_PROP]);
  const isManaged = (item) => isXParent(item) || isTiParent(item) || isGenericParent(item) || Boolean(props(item)[PARENT_PROP]) || isPackageComponent(props(item));
  const isConfiguredRequiredSkate = (item) => {
    const p = props(item);
    const steelChoice = String(p['Steel choice'] || '').trim();
    const runnerSize = String(p['Runner size'] || '').trim();
    const steelPackage = String(p['Steel package'] || '').trim().toLowerCase();
    const regularSteelSelection =
      /^BAUER FLY-(?:TI|X)$/i.test(steelChoice) &&
      Boolean(runnerSize) &&
      steelPackage === 'included with regular skate package';

    return Boolean(
      p[X_PACKAGE_PROP] ||
      p[TI_PACKAGE_PROP] ||
      idNum(p[STEEL_VARIANT_PROP]) ||
      regularSteelSelection
    );
  };
  const incompleteRequiredSkates = (cart) =>
    (cart?.items || []).filter((item) =>
      REQUIRED_CONFIGURATION_HANDLES.has(String(item?.handle || '')) && !isConfiguredRequiredSkate(item)
    );
  let syncPromise = null;
  let syncTimer = null;
  let lastCart = null;
  let blockingError = '';
  let externalMutationPending = false;
  let cartPageReloading = false;
  const CHECKOUT_SELECTOR = '#checkout, #CartDrawer-Checkout, #CartPage-Checkout';
  const isCartPage = () => location.pathname.replace(/\/$/, '').endsWith('/cart');

  document.documentElement.classList.add('sz-cart-checking');

  const parsePayload = (body) => {
    if (typeof body !== 'string') return null;
    try { return JSON.parse(body); } catch (_error) { return null; }
  };

  const injectConfigurationLinks = (payload) => {
    if (!Array.isArray(payload?.items) || payload.items.length < 2) return false;
    const main = payload.items[0];
    const mainProps = { ...props(main) };
    const linkedChildren = [];

    payload.items.slice(1).forEach((child) => {
      const childProps = { ...props(child) };
      if (childProps['Service for']) linkedChildren.push({ child, childProps, kind: 'service' });
      else if (childProps['Runner for'] && !isPackageComponent(childProps)) linkedChildren.push({ child, childProps, kind: 'steel' });
    });

    if (!linkedChildren.length) return false;
    const key = mainProps[CONFIG_PROP] || uniqueKey(main.id);
    mainProps[CONFIG_PROP] = key;

    linkedChildren.forEach(({ child, childProps, kind }) => {
      childProps[PARENT_PROP] = key;
      if (kind === 'service') {
        childProps[COMPONENT_PROP] = 'Professional setup service';
        mainProps[SERVICE_VARIANT_PROP] = String(idNum(child.id));
        mainProps[SERVICE_FOR_PROP] = childProps['Service for'] || '';
        if (childProps['ProSharp profile']) mainProps[SERVICE_PROFILE_PROP] = childProps['ProSharp profile'];
        if (childProps['Sharpening hollow']) mainProps[SERVICE_SHARPEN_PROP] = childProps['Sharpening hollow'];
      } else {
        childProps[COMPONENT_PROP] = 'BAUER steel runner';
        mainProps[STEEL_VARIANT_PROP] = String(idNum(child.id));
        mainProps[STEEL_FOR_PROP] = childProps['Runner for'] || '';
      }
      child.properties = childProps;
    });
    main.properties = mainProps;
    return true;
  };

  window.fetch = async (input, options = {}) => {
    if (isAdd(input) && typeof options?.body === 'string') {
      const payload = parsePayload(options.body);
      if (payload && injectConfigurationLinks(payload)) options = { ...options, body: JSON.stringify(payload) };
    }
    const response = await originalFetch(input, options);
    if (isMutation(input)) {
      externalMutationPending = true;
      scheduleSync(70);
    }
    return response;
  };

  const fetchCart = async () => {
    const response = await originalFetch(`${rootUrl()}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('Could not read cart.');
    return response.json();
  };

  const changeLine = async (key, quantity) => {
    const response = await originalFetch(`${rootUrl()}cart/change.js`, {
      method: 'POST', credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity }),
    });
    const cart = await response.json();
    if (!response.ok || cart?.status) throw new Error(cart?.description || cart?.message || 'Could not synchronize configured cart items.');
    return cart;
  };

  const removeLinesAtomic = async (keys) => {
    const uniqueKeys = Array.from(new Set((keys || []).filter(Boolean)));
    if (!uniqueKeys.length) return fetchCart();

    const updates = {};
    uniqueKeys.forEach((key) => { updates[key] = 0; });

    const response = await originalFetch(`${rootUrl()}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ updates }),
    });

    const cart = await response.json();
    if (!response.ok || cart?.status) {
      throw new Error(cart?.description || cart?.message || 'Could not remove the selected cart item.');
    }
    return cart;
  };

  const addItem = async (id, quantity, properties) => {
    const response = await originalFetch(`${rootUrl()}cart/add.js`, {
      method: 'POST', credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id, quantity, properties }] }),
    });
    const data = await response.json();
    if (!response.ok || data?.status) throw new Error(data?.description || data?.message || 'Could not restore a configured cart item.');
    return data;
  };

  const changeLineProperties = async (key, quantity, properties) => {
    const response = await originalFetch(`${rootUrl()}cart/change.js`, {
      method: 'POST', credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity, properties }),
    });
    const cart = await response.json();
    if (!response.ok || cart?.status) throw new Error(cart?.description || cart?.message || 'Could not update the linked cart item.');
    return cart;
  };

  const discountCodes = (cart) => (cart?.discount_codes || []).map((entry) => typeof entry === 'string' ? entry : entry?.code).filter(Boolean);

  const syncDiscounts = async (cart) => {
    const shouldX = (cart.items || []).some(isXParent);
    const shouldTi = (cart.items || []).some(isTiParent);
    const current = discountCodes(cart);
    const desired = current.filter((code) => !MANAGED_CODES.has(String(code).toUpperCase()));
    if (shouldX) desired.push(X_CODE);
    if (shouldTi) desired.push(TI_CODE);
    const next = Array.from(new Set(desired));
    const a = [...current].sort().join('|').toUpperCase();
    const b = [...next].sort().join('|').toUpperCase();
    if (a === b) return cart;
    const response = await originalFetch(`${rootUrl()}cart/update.js`, {
      method: 'POST', credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ discount: next.join(',') }),
    });
    const updated = await response.json();
    if (!response.ok || updated?.status) throw new Error(updated?.description || updated?.message || 'Could not verify package pricing.');
    return updated;
  };

  const packageLinked = (cart, parent, packageProp, componentProp) => {
    const key = props(parent)[packageProp];
    return (cart.items || []).find((item) => props(item)[componentProp] && props(item)[packageProp] === key) || null;
  };

  const expectedPackageRunner = async (parent, steel) => {
    const runnerSize = props(parent)['Runner size'] || '';
    const holder = Number.parseInt(String(runnerSize).split('/')[0].replace(/[^\d]/g, ''), 10) || 0;
    if (!holder) return null;
    const handle = steel === 'FLY-X' ? 'bauer-fly-x-hockey-skate-runners' : 'bauer-fly-ti-hockey-skate-runners';
    const response = await originalFetch(`${rootUrl()}products/${handle}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Could not verify BAUER ${steel} runner inventory.`);
    const product = await response.json();
    return (product.variants || []).find((variant) => Number.parseInt(String(variant.title || variant.option1 || '').replace(/[^\d]/g, ''), 10) === holder) || null;
  };

  const restorePackageRunner = async (parent, steel, packageProp, componentProp) => {
    const runner = await expectedPackageRunner(parent, steel);
    if (!runner?.id || runner.available === false) throw new Error(`The matched BAUER ${steel} runner is no longer available for this skate size.`);
    const p = props(parent);
    await addItem(runner.id, Number(parent.quantity || 1), {
      'Runner for': `${parent.product_title || 'Configured BAUER skate'}${parent.variant_title ? ` — ${parent.variant_title}` : ''}`,
      'Matched runner size': p['Runner size'] || String(runner.title || ''),
      'Steel package': `${steel} package runner — price adjusted with skate`,
      [packageProp]: p[packageProp], [componentProp]: `BAUER ${steel} runner`,
    });
  };

  const syncPackageParent = async (cart, parent, steel) => {
    const packageProp = steel === 'FLY-X' ? X_PACKAGE_PROP : TI_PACKAGE_PROP;
    const componentProp = steel === 'FLY-X' ? X_COMPONENT_PROP : TI_COMPONENT_PROP;
    const child = packageLinked(cart, parent, packageProp, componentProp);
    if (!child) {
      await restorePackageRunner(parent, steel, packageProp, componentProp);
      return { changed: true, cart: await fetchCart() };
    }
    if (Number(child.quantity || 0) !== Number(parent.quantity || 0)) return { changed: true, cart: await changeLine(child.key, Number(parent.quantity || 0)) };
    return { changed: false, cart };
  };

  const genericChildren = (cart, key) => (cart.items || []).filter((item) => props(item)[PARENT_PROP] === key);

  const restoreGenericChildren = async (cart, parent) => {
    const p = props(parent);
    const key = p[CONFIG_PROP];
    let changed = false;
    let children = genericChildren(cart, key);
    const serviceVariant = idNum(p[SERVICE_VARIANT_PROP]);
    if (serviceVariant && !children.some((item) => idNum(item.variant_id || item.id) === serviceVariant && props(item)[COMPONENT_PROP] === 'Professional setup service')) {
      const serviceProps = { 'Service for': p[SERVICE_FOR_PROP] || `${parent.product_title || 'Configured product'}${parent.variant_title ? ` — ${parent.variant_title}` : ''}`, [PARENT_PROP]: key, [COMPONENT_PROP]: 'Professional setup service' };
      if (p[SERVICE_PROFILE_PROP]) serviceProps['ProSharp profile'] = p[SERVICE_PROFILE_PROP];
      if (p[SERVICE_SHARPEN_PROP]) serviceProps['Sharpening hollow'] = p[SERVICE_SHARPEN_PROP];
      await addItem(serviceVariant, Number(parent.quantity || 1), serviceProps);
      cart = await fetchCart(); changed = true; children = genericChildren(cart, key);
    }
    const steelVariant = idNum(p[STEEL_VARIANT_PROP]);
    if (steelVariant && !children.some((item) => idNum(item.variant_id || item.id) === steelVariant && props(item)[COMPONENT_PROP] === 'BAUER steel runner')) {
      await addItem(steelVariant, Number(parent.quantity || 1), {
        'Runner for': p[STEEL_FOR_PROP] || `${parent.product_title || 'Configured BAUER skate'}${parent.variant_title ? ` — ${parent.variant_title}` : ''}`,
        'Matched runner size': p['Runner size'] || '', [PARENT_PROP]: key, [COMPONENT_PROP]: 'BAUER steel runner',
      });
      cart = await fetchCart(); changed = true;
    }
    for (const child of genericChildren(cart, key)) {
      if (Number(child.quantity || 0) !== Number(parent.quantity || 0)) { cart = await changeLine(child.key, Number(parent.quantity || 0)); changed = true; }
    }
    return { changed, cart };
  };

  const removeOrphans = async (cart) => {
    const genericKeys = new Set((cart.items || []).filter(isGenericParent).map((item) => props(item)[CONFIG_PROP]));
    const xKeys = new Set((cart.items || []).filter(isXParent).map((item) => props(item)[X_PACKAGE_PROP]));
    const tiKeys = new Set((cart.items || []).filter(isTiParent).map((item) => props(item)[TI_PACKAGE_PROP]));
    let changed = false;
    for (const item of [...(cart.items || [])]) {
      const p = props(item);
      const orphan = (p[PARENT_PROP] && !genericKeys.has(p[PARENT_PROP])) || (p[X_COMPONENT_PROP] && p[X_PACKAGE_PROP] && !xKeys.has(p[X_PACKAGE_PROP])) || (p[TI_COMPONENT_PROP] && p[TI_PACKAGE_PROP] && !tiKeys.has(p[TI_PACKAGE_PROP]));
      if (orphan) { cart = await changeLine(item.key, 0); changed = true; }
    }
    return { changed, cart: changed ? await fetchCart() : cart };
  };

  const refreshCartPageFromServer = async (cart) => {
    if (!isCartPage()) return false;

    const currentRoot = document.getElementById('cart_page-data');
    const sectionId = document.getElementById('main-cart-items')?.dataset?.id;
    if (!currentRoot || !sectionId) return false;

    const response = await originalFetch(
      `${rootUrl()}cart?section_id=${encodeURIComponent(sectionId)}&_sz=${Date.now()}`,
      {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }
    );

    if (!response.ok) throw new Error('Could not refresh the cart display.');

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const nextRoot = html.querySelector('#cart_page-data');
    if (!nextRoot) throw new Error('Could not refresh the cart display.');

    currentRoot.replaceWith(nextRoot);
    if (cart) {
      lastCart = cart;
      decorate(cart);
    }
    return true;
  };

  const updateCartMeta = (cart) => {
    if (!cart) return;
    const total = money(cart.total_price || 0);
    document.querySelectorAll('#checkout, #CartPage-Checkout, #CartDrawer-Checkout').forEach((button) => {
      button.textContent = `Checkout · ${total}`;
    });
    document.querySelectorAll('.totals__total-value').forEach((node) => {
      node.textContent = total;
    });
    document.querySelectorAll('.sz-cart-page-count, .sz-cart-drawer-count').forEach((node) => {
      node.textContent = String(Number(cart.item_count || 0));
    });
    document.querySelectorAll('free-shipping-bar').forEach((bar) => {
      bar.setAttribute('data-total-price', String(Number(cart.total_price || 0)));
    });
  };

  const notifyCartUpdate = (cart) => {
    updateCartMeta(cart);
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });
  };

  const setBusy = (busy) => {
    document.documentElement.classList.toggle('sz-cart-syncing', busy);
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((button) => {
      if (busy) {
        button.dataset.szCheckoutPending = 'true';
        if (!button.hasAttribute('data-sz-was-disabled')) button.dataset.szWasDisabled = button.disabled ? 'true' : 'false';
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      } else {
        delete button.dataset.szCheckoutPending;
        if (blockingError || Number(lastCart?.item_count || 0) === 0) {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
        }
        delete button.dataset.szWasDisabled;
        button.removeAttribute('aria-busy');
      }
    });
  };

  const checkoutReady = () =>
    !blockingError &&
    !syncPromise &&
    !document.documentElement.classList.contains('sz-cart-checking') &&
    !document.documentElement.classList.contains('sz-cart-syncing');

  const removeDecorations = () => {
    document.querySelectorAll('.sz-cart-package-summary, .sz-cart-component-note, .sz-cart-config-notice, .sz-cart-incomplete-note, .sz-cart-checkout-warning').forEach((node) => node.remove());
    document.querySelectorAll('.sz-cart-config-parent, .sz-cart-component, .sz-cart-package-runner, .sz-cart-paid-component, .sz-cart-incomplete-row').forEach((node) => node.classList.remove('sz-cart-config-parent', 'sz-cart-component', 'sz-cart-package-runner', 'sz-cart-paid-component', 'sz-cart-incomplete-row'));
    document.documentElement.classList.remove('sz-cart-incomplete-config');
  };
  const rowNodes = (index) => document.querySelectorAll(`#CartItem-${index}, #CartDrawer-Item-${index}`);
  const componentNoteText = (item) => {
    const p = props(item);
    if (p[X_COMPONENT_PROP] || p[TI_COMPONENT_PROP]) return 'Included in package total · quantity follows skates';
    if (p[COMPONENT_PROP] === 'BAUER steel runner') return 'Required steel · priced separately · quantity follows skates';
    if (p[COMPONENT_PROP] === 'Professional setup service') return 'Professional setup · quantity follows the product above';
    return 'Linked to the product above';
  };
  const parentPackageChild = (cart, parent) => isXParent(parent) ? packageLinked(cart, parent, X_PACKAGE_PROP, X_COMPONENT_PROP) : isTiParent(parent) ? packageLinked(cart, parent, TI_PACKAGE_PROP, TI_COMPONENT_PROP) : null;

  const decorate = (cart) => {
    lastCart = cart;
    updateCartMeta(cart);
    removeDecorations();
    const managed = (cart.items || []).some(isManaged);
    document.documentElement.classList.toggle('sz-managed-cart', managed);
    document.documentElement.classList.remove('sz-cart-checking');
    (cart.items || []).forEach((item, idx) => {
      const p = props(item); const index = idx + 1;
      const isChild = Boolean(p[PARENT_PROP] || p[X_COMPONENT_PROP] || p[TI_COMPONENT_PROP]);
      rowNodes(index).forEach((row) => {
        if (isChild) {
          row.classList.add('sz-cart-component');
          if (p[X_COMPONENT_PROP] || p[TI_COMPONENT_PROP]) row.classList.add('sz-cart-package-runner'); else row.classList.add('sz-cart-paid-component');
          const details = row.querySelector('.cart-item__details');
          if (details && !details.querySelector('.sz-cart-component-note')) {
            const note = document.createElement('p');
            note.className = 'sz-cart-component-note';
            note.textContent = componentNoteText(item);
            details.append(note);
          }
          return;
        }
        if (!isXParent(item) && !isTiParent(item) && !isGenericParent(item)) return;
        row.classList.add('sz-cart-config-parent');
        const details = row.querySelector('.cart-item__details');
        if (!details || details.querySelector('.sz-cart-package-summary')) return;
        let eyebrow = 'CONFIGURED ITEM'; let message = 'Linked setup items appear directly below and stay matched to this quantity.'; let total = '';
        if (isXParent(item) || isTiParent(item)) {
          const child = parentPackageChild(cart, item); const isX = isXParent(item);
          eyebrow = isX ? 'FLY-X PACKAGE · SAVE $80' : 'FLY-TI PACKAGE';
          message = isX ? 'Matched FLY-X runners are included in this package total.' : 'Matched FLY-TI runners are included in this package total.';
          if (child) total = `Package total ${money(Number(item.final_line_price || 0) + Number(child.final_line_price || 0))}`;
        } else if (String(p['Steel package'] || '').toLowerCase().includes('boot-only')) { eyebrow = 'BOOT-ONLY SALE'; message = 'Steel is priced separately below and stays matched to the skate quantity.'; }
        else if (idNum(p[SERVICE_VARIANT_PROP])) { eyebrow = 'PROFESSIONAL SETUP LINKED'; message = 'The service below stays matched to this product quantity.'; }
        const summary = document.createElement('div'); summary.className = 'sz-cart-package-summary';
        summary.innerHTML = `<span>${eyebrow}</span>${total ? `<strong>${total}</strong>` : ''}<small>${message}</small>`;
        const name = details.querySelector('.cart-item__name'); if (name) name.insertAdjacentElement('afterend', summary); else details.prepend(summary);
      });
    });
    const incomplete = incompleteRequiredSkates(cart);
    const hasIncomplete = incomplete.length > 0;
    document.documentElement.classList.toggle('sz-cart-incomplete-config', hasIncomplete);

    if (hasIncomplete) {
      const first = incomplete[0];
      const itemIndex = (cart.items || []).findIndex((item) => item.key === first.key);
      if (itemIndex >= 0) {
        rowNodes(itemIndex + 1).forEach((row) => {
          row.classList.add('sz-cart-incomplete-row');
          const details = row.querySelector('.cart-item__details');
          if (details && !details.querySelector('.sz-cart-incomplete-note')) {
            const note = document.createElement('div');
            note.className = 'sz-cart-incomplete-note';
            note.setAttribute('role', 'alert');

            const strong = document.createElement('strong');
            strong.textContent = 'STEEL REQUIRED';

            const copy = document.createElement('span');
            copy.textContent = 'Choose compatible BAUER steel for this skate before checkout.';

            const link = document.createElement('a');
            link.href = first.url || (first.handle ? rootUrl() + 'products/' + first.handle : '#');
            link.textContent = 'Choose steel';

            note.append(strong, copy, link);
            details.append(note);
          }
        });
      }

      document.querySelectorAll('#main-cart-footer .cart__blocks, .drawer__footer').forEach((host) => {
        if (host.querySelector('.sz-cart-checkout-warning')) return;

        const warning = document.createElement('div');
        warning.className = 'sz-cart-checkout-warning';
        warning.setAttribute('role', 'alert');

        const strong = document.createElement('strong');
        strong.textContent = 'Complete your skate setup';

        const copy = document.createElement('span');
        copy.textContent = 'Steel is required before checkout.';

        const link = document.createElement('a');
        link.href = first.url || (first.handle ? rootUrl() + 'products/' + first.handle : '#');
        link.textContent = 'Choose steel';

        warning.append(strong, copy, link);
        host.prepend(warning);
      });
    }

    document.dispatchEvent(new CustomEvent('sportszone:cart-verified', { detail: { cart } }));

    // Cart section refreshes can replace the checkout button with server HTML that starts disabled.
    // Re-apply the verified checkout state after those DOM replacements finish.
    if (!syncPromise && !document.documentElement.classList.contains('sz-cart-syncing')) {
      setBusy(false);
    }
  };

  const syncAll = async (initialCart = null) => {
    if (syncPromise) return syncPromise;
    syncPromise = (async () => {
      setBusy(true); blockingError = ''; let cart = initialCart || await fetchCart(); let changed = false;
      const orphanResult = await removeOrphans(cart); cart = orphanResult.cart; changed ||= orphanResult.changed;
      for (const parent of [...(cart.items || []).filter(isXParent)]) { const result = await syncPackageParent(cart, parent, 'FLY-X'); cart = result.cart; changed ||= result.changed; }
      for (const parent of [...(cart.items || []).filter(isTiParent)]) { const result = await syncPackageParent(cart, parent, 'FLY-TI'); cart = result.cart; changed ||= result.changed; }
      for (const parent of [...(cart.items || []).filter(isGenericParent)]) { const result = await restoreGenericChildren(cart, parent); cart = result.cart; changed ||= result.changed; }
      const beforeCodes = discountCodes(cart).sort().join('|');
      cart = await syncDiscounts(cart);
      const afterCodes = discountCodes(cart).sort().join('|');
      if (beforeCodes !== afterCodes) changed = true;

      const incomplete = incompleteRequiredSkates(cart);
      blockingError = incomplete.length
        ? 'Steel is required for one or more skates before checkout. Remove the skate or choose the correct steel.'
        : '';

      lastCart = cart;
      notifyCartUpdate(cart);

      const shouldRefresh = externalMutationPending && isCartPage();
      externalMutationPending = false;

      if (shouldRefresh && !cartPageReloading) {
        cartPageReloading = true;
        try {
          const refreshed = await refreshCartPageFromServer(cart);
          if (!refreshed) decorate(cart);
        } finally {
          cartPageReloading = false;
        }
        return cart;
      }

      window.setTimeout(() => decorate(cart), changed ? 80 : 0);
      return cart;
    })().catch((error) => {
      blockingError = error?.message || 'We could not verify the linked configuration.'; console.error('SportsZone cart configuration error', error); if (lastCart) decorate(lastCart); throw error;
    }).finally(() => { setBusy(false); syncPromise = null; });
    return syncPromise;
  };

  const scheduleSync = (delay = 90) => { clearTimeout(syncTimer); syncTimer = setTimeout(() => { syncAll().catch(() => {}); }, delay); };

  /* SportsZone linked package removal */
  const removePackageWithComponents = async (parentKey, initialCart = null) => {
    const cart = initialCart || await fetchCart();
    const parent = (cart.items || []).find((item) => item.key === parentKey);

    if (!parent || (!isXParent(parent) && !isTiParent(parent))) {
      throw new Error('This skate package changed. Refresh the cart and try again.');
    }

    const parentProps = props(parent);
    const packageProp = isXParent(parent) ? X_PACKAGE_PROP : TI_PACKAGE_PROP;
    const componentProp = isXParent(parent) ? X_COMPONENT_PROP : TI_COMPONENT_PROP;
    const packageKey = parentProps[packageProp];
    const configKey = parentProps[CONFIG_PROP];

    const linkedItems = (cart.items || []).filter((item) => {
      if (item.key === parent.key) return false;

      const itemProps = props(item);
      const packageComponent =
        packageKey &&
        itemProps[componentProp] &&
        itemProps[packageProp] === packageKey;

      const configuredComponent =
        configKey &&
        itemProps[PARENT_PROP] === configKey;

      return Boolean(packageComponent || configuredComponent);
    });

    return removeLinesAtomic([parent.key, ...linkedItems.map((item) => item.key)]);
  };

  /* SportsZone linked child removal */
  const linkedParentFor = (cart, child) => {
    const p = props(child);

    if (p[TI_COMPONENT_PROP] && p[TI_PACKAGE_PROP]) {
      return (cart.items || []).find((item) => isTiParent(item) && props(item)[TI_PACKAGE_PROP] === p[TI_PACKAGE_PROP]) || null;
    }

    if (p[X_COMPONENT_PROP] && p[X_PACKAGE_PROP]) {
      return (cart.items || []).find((item) => isXParent(item) && props(item)[X_PACKAGE_PROP] === p[X_PACKAGE_PROP]) || null;
    }

    if (p[PARENT_PROP]) {
      return (cart.items || []).find((item) => props(item)[CONFIG_PROP] === p[PARENT_PROP] && !props(item)[PARENT_PROP]) || null;
    }

    return null;
  };

  const detachLinkedChild = async (cart, parent, child) => {
    if (!parent) return cart;

    const childProps = props(child);
    const parentProps = { ...props(parent) };

    if (childProps[TI_COMPONENT_PROP]) {
      delete parentProps[TI_PACKAGE_PROP];
      delete parentProps['Steel choice'];
      delete parentProps['Steel package'];
    } else if (childProps[X_COMPONENT_PROP]) {
      delete parentProps[X_PACKAGE_PROP];
      delete parentProps['Steel choice'];
      delete parentProps['Steel package'];
    } else if (childProps[COMPONENT_PROP] === 'Professional setup service') {
      delete parentProps[SERVICE_VARIANT_PROP];
      delete parentProps[SERVICE_FOR_PROP];
      delete parentProps[SERVICE_PROFILE_PROP];
      delete parentProps[SERVICE_SHARPEN_PROP];
    } else if (childProps[COMPONENT_PROP] === 'BAUER steel runner') {
      delete parentProps[STEEL_VARIANT_PROP];
      delete parentProps[STEEL_FOR_PROP];
    }

    return changeLineProperties(parent.key, Number(parent.quantity || 1), parentProps);
  };

  const removeLinkedChild = async (itemKey, initialCart = null) => {
    let cart = initialCart || await fetchCart();
    const child = (cart.items || []).find((item) => item.key === itemKey);
    if (!child) throw new Error('This linked cart item changed. Refresh the cart and try again.');

    const childProps = { ...props(child) };
    const linked =
      Boolean(childProps[PARENT_PROP]) ||
      Boolean(childProps[X_COMPONENT_PROP]) ||
      Boolean(childProps[TI_COMPONENT_PROP]);

    if (!linked) return removeLinesAtomic([child.key]);

    const parentSnapshot = linkedParentFor(cart, child);
    const parentPackageKey =
      childProps[TI_PACKAGE_PROP] ||
      childProps[X_PACKAGE_PROP] ||
      childProps[PARENT_PROP] ||
      '';

    cart = await removeLinesAtomic([child.key]);

    let currentParent = null;
    if (childProps[TI_COMPONENT_PROP] && childProps[TI_PACKAGE_PROP]) {
      currentParent = (cart.items || []).find((item) =>
        isTiParent(item) && props(item)[TI_PACKAGE_PROP] === childProps[TI_PACKAGE_PROP]
      ) || null;
    } else if (childProps[X_COMPONENT_PROP] && childProps[X_PACKAGE_PROP]) {
      currentParent = (cart.items || []).find((item) =>
        isXParent(item) && props(item)[X_PACKAGE_PROP] === childProps[X_PACKAGE_PROP]
      ) || null;
    } else if (childProps[PARENT_PROP]) {
      currentParent = (cart.items || []).find((item) =>
        props(item)[CONFIG_PROP] === childProps[PARENT_PROP] && !props(item)[PARENT_PROP]
      ) || null;
    }

    if (!currentParent && parentSnapshot) {
      currentParent = (cart.items || []).find((item) => item.variant_id === parentSnapshot.variant_id) || null;
    }

    if (currentParent) {
      cart = await detachLinkedChild(cart, currentParent, { ...child, properties: childProps });
    }

    return cart;
  };

  document.addEventListener('click', (event) => {
    const nativeRemove = event.target.closest?.('cart-remove-button');
    const control = nativeRemove;
    if (!control) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (document.documentElement.classList.contains('sz-cart-syncing')) return;

    const key = control.dataset.key || nativeRemove?.dataset.key || '';
    const line = Number(control.dataset.index || nativeRemove?.dataset.index || 0);

    clearTimeout(syncTimer);
    setBusy(true);

    (async () => {
      if (syncPromise) {
        try { await syncPromise; } catch (_error) {}
      }

      const useCachedCart = Boolean(lastCart && !externalMutationPending);
      let cart = useCachedCart ? lastCart : await fetchCart();
      let item = key ? (cart.items || []).find((entry) => entry.key === key) : null;

      if (!item && useCachedCart) {
        cart = await fetchCart();
        item = key ? (cart.items || []).find((entry) => entry.key === key) : null;
      }

      if (!item && line) item = (cart.items || [])[line - 1] || null;
      if (!item) throw new Error('This cart item changed. Please try removing it again.');

      externalMutationPending = false;

      const p = props(item);
      const isLinkedChild = Boolean(p[PARENT_PROP] || p[X_COMPONENT_PROP] || p[TI_COMPONENT_PROP]);

      let mutationCart;
      if (isXParent(item) || isTiParent(item)) {
        mutationCart = await removePackageWithComponents(item.key, cart);
      } else if (isLinkedChild) {
        mutationCart = await removeLinkedChild(item.key, cart);
      } else {
        mutationCart = await removeLinesAtomic([item.key]);
      }

      lastCart = mutationCart || cart;
      lastCart = await syncAll(lastCart);

      if (isCartPage() && !cartPageReloading) {
        cartPageReloading = true;
        try {
          const refreshed = await refreshCartPageFromServer(lastCart);
          if (!refreshed) decorate(lastCart);
        } finally {
          cartPageReloading = false;
        }
      }
    })()
      .catch((error) => {
        blockingError = error?.message || 'We could not remove this cart item.';
        console.error('SportsZone cart removal error', error);
        if (lastCart) decorate(lastCart);
      })
      .finally(() => setBusy(false));
  }, true);

  document.addEventListener('click', (event) => {
    const checkout = event.target.closest?.(CHECKOUT_SELECTOR);
    if (!checkout) return;
    if (!checkoutReady() || checkout.dataset.szCheckoutPending === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      syncAll().catch(() => {});
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const submitter = event.submitter;
    if (!submitter || !submitter.matches?.(CHECKOUT_SELECTOR)) return;

    if (!checkoutReady() || submitter.dataset.szCheckoutPending === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      syncAll().catch(() => {});
      return;
    }

    const form = event.target;
    if (form?.dataset?.szCheckoutSubmitting === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (form?.dataset) form.dataset.szCheckoutSubmitting = 'true';
    submitter.setAttribute('aria-busy', 'true');
  }, true);

  window.addEventListener('pageshow', () => {
    document.querySelectorAll('form[data-sz-checkout-submitting="true"]').forEach((form) => {
      delete form.dataset.szCheckoutSubmitting;
    });
    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((button) => {
      if (button.dataset.szCheckoutPending !== 'true' && !document.documentElement.classList.contains('sz-cart-syncing')) {
        button.removeAttribute('aria-busy');
      }
    });
  });
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => { if (event?.source !== SOURCE) scheduleSync(120); });
  const observer = new MutationObserver((mutations) => {
    if (!lastCart) return;
    const changed = mutations.some((mutation) => Array.from(mutation.addedNodes || []).some((node) => node.nodeType === 1 && (node.matches?.('.cart-item') || node.querySelector?.('.cart-item'))));
    if (changed) setTimeout(() => decorate(lastCart), 30);
  });
  const observerTargets = [
    document.getElementById('MainContent'),
    document.querySelector('cart-drawer')
  ].filter(Boolean);
  const uniqueObserverTargets = Array.from(new Set(observerTargets));
  uniqueObserverTargets.forEach((target) => observer.observe(target, { childList: true, subtree: true }));
  if (!uniqueObserverTargets.length && document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }
  window.SportsZoneCartConfig = {
    initialized: true,
    sync: syncAll,
    decorate: () => lastCart && decorate(lastCart),
    getCart: () => lastCart,
    checkoutReady
  };
  scheduleSync(30);
})();
